// src/pages/Browse.jsx
import { useState, useEffect } from "react";
import { collection, query, where, orderBy, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { Link } from "react-router-dom";

export default function Browse() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const q = query(
      collection(db, "cats"),
      where("status", "==", "available"),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(q, snap => {
      setCats(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  const filtered = filter === "all" ? cats
    : cats.filter(c => c.ageGroup === filter);

  return (
    <div style={styles.page}>
      {/* Hero */}
      <div style={styles.hero}>
        <div style={styles.heroInner}>
          <p style={styles.heroEyebrow}>🐾 Find your companion</p>
          <h1 style={styles.heroTitle}>Every Cat Deserves<br/><em>a Loving Home</em></h1>
          <p style={styles.heroSub}>Browse cats looking for their forever family. Click any cat to learn more.</p>
        </div>
        <div style={styles.heroPaw}>🐾</div>
      </div>

      {/* Filters */}
      <div style={styles.filterBar}>
        {[["all","All Cats"],["kitten","Kittens"],["adult","Adults"],["senior","Seniors"]].map(([val,label])=>(
          <button key={val}
            style={{...styles.filterBtn, ...(filter===val ? styles.filterActive:{})}}
            onClick={()=>setFilter(val)}>
            {label}
          </button>
        ))}
        <span style={styles.count}>{filtered.length} cat{filtered.length!==1?"s":""} available</span>
      </div>

      {/* Grid */}
      {loading ? (
        <div style={styles.loadingWrap}>
          <div className="spinner"/>
          <p style={{color:"var(--muted)",marginTop:12}}>Loading cats…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div style={styles.empty}>
          <p style={{fontSize:48}}>🐱</p>
          <p style={{fontSize:18,fontWeight:600,marginTop:12}}>No cats available right now</p>
          <p style={{color:"var(--muted)",marginTop:6}}>Check back soon or <Link to="/submit" style={{color:"var(--rust)"}}>rehome your cat</Link></p>
        </div>
      ) : (
        <div style={styles.grid}>
          {filtered.map((cat, i) => (
            <CatCard key={cat.id} cat={cat} delay={i * 60} />
          ))}
        </div>
      )}
    </div>
  );
}

function CatCard({ cat, delay }) {
  const [hovered, setHovered] = useState(false);

  return (
    <Link to={`/cat/${cat.id}`} style={{ textDecoration:"none" }}
      className="animate-up"
      style={{ animationDelay: `${delay}ms` }}>
      <div
        style={{...styles.card, ...(hovered ? styles.cardHover:{})}}
        onMouseEnter={()=>setHovered(true)}
        onMouseLeave={()=>setHovered(false)}>
        {/* Photo */}
        <div style={styles.photoWrap}>
          {cat.photos?.[0] ? (
            <img src={cat.photos[0]} alt={cat.name}
              style={{...styles.photo, transform: hovered?"scale(1.06)":"scale(1)"}}/>
          ) : (
            <div style={styles.noPhoto}>🐱</div>
          )}
          <div style={styles.ageBadge}>{cat.ageGroup || "adult"}</div>
        </div>
        {/* Info */}
        <div style={styles.cardBody}>
          <div style={styles.cardTop}>
            <h3 style={styles.catName}>{cat.name}</h3>
            <span style={styles.gender}>{cat.gender === "male" ? "♂" : "♀"}</span>
          </div>
          <p style={styles.breed}>{cat.breed || "Mixed breed"} · {cat.age}</p>
          <p style={styles.desc}>{cat.shortDesc || cat.description?.slice(0,80)}…</p>
          <div style={styles.tags}>
            {(cat.traits || []).slice(0,3).map(t => (
              <span key={t} className="tag">{t}</span>
            ))}
          </div>
          <div style={styles.adoptBtn}>
            <span>Meet {cat.name} →</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

const styles = {
  page: { minHeight:"100vh", paddingBottom:80 },

  hero: {
    background: "linear-gradient(135deg, var(--deep) 0%, var(--mid) 100%)",
    padding:"64px 40px 56px",
    position:"relative", overflow:"hidden",
    display:"flex", alignItems:"center",
  },
  heroInner: { position:"relative", zIndex:1, maxWidth:600 },
  heroEyebrow: { color:"var(--rust-l)", fontSize:14, fontWeight:600, letterSpacing:2, textTransform:"uppercase", marginBottom:14 },
  heroTitle: { fontFamily:"'Playfair Display',serif", fontSize:"clamp(36px,5vw,58px)", color:"#fff", lineHeight:1.1, marginBottom:16 },
  heroSub: { color:"rgba(255,255,255,0.65)", fontSize:17, fontWeight:300, lineHeight:1.6 },
  heroPaw: { position:"absolute", right:40, bottom:-20, fontSize:160, opacity:0.06, transform:"rotate(15deg)" },

  filterBar: {
    padding:"24px 40px", display:"flex", alignItems:"center", gap:10, flexWrap:"wrap",
    borderBottom:"1px solid var(--peach)",
  },
  filterBtn: {
    padding:"8px 20px", borderRadius:50, border:"2px solid var(--peach)",
    background:"transparent", color:"var(--muted)", fontSize:14, fontWeight:500,
    cursor:"pointer", transition:"all 0.2s",
  },
  filterActive: { background:"var(--rust)", color:"#fff", borderColor:"var(--rust)" },
  count: { marginLeft:"auto", color:"var(--muted)", fontSize:14 },

  loadingWrap: { padding:80, textAlign:"center" },
  empty: { padding:80, textAlign:"center" },

  grid: {
    padding:"36px 40px",
    display:"grid",
    gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))",
    gap:28,
  },

  card: {
    background:"#fff", borderRadius:20,
    boxShadow:"var(--shadow-s)",
    overflow:"hidden",
    transition:"all 0.3s",
    cursor:"pointer",
  },
  cardHover: {
    boxShadow:"var(--shadow-l)",
    transform:"translateY(-6px)",
  },
  photoWrap: {
    position:"relative", height:220, overflow:"hidden",
    background:"var(--warm)",
  },
  photo: {
    width:"100%", height:"100%", objectFit:"cover",
    transition:"transform 0.4s ease",
  },
  noPhoto: {
    width:"100%", height:"100%",
    display:"flex", alignItems:"center", justifyContent:"center",
    fontSize:64, background:"var(--warm)",
  },
  ageBadge: {
    position:"absolute", top:12, left:12,
    background:"rgba(0,0,0,0.55)", color:"#fff",
    fontSize:11, fontWeight:600, letterSpacing:1,
    textTransform:"uppercase", padding:"4px 10px", borderRadius:20,
  },
  cardBody: { padding:"18px 20px 20px" },
  cardTop: { display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 },
  catName: { fontFamily:"'Playfair Display',serif", fontSize:20, color:"var(--deep)" },
  gender: { fontSize:20, color:"var(--muted)" },
  breed: { fontSize:13, color:"var(--muted)", marginBottom:8 },
  desc: { fontSize:14, color:"var(--text)", lineHeight:1.5, marginBottom:12 },
  tags: { display:"flex", gap:6, flexWrap:"wrap", marginBottom:14 },
  adoptBtn: {
    color:"var(--rust)", fontSize:14, fontWeight:600,
    display:"flex", alignItems:"center", gap:4,
  },
};
